import React, { useState, useEffect } from 'react';
import { fetchProducts, deleteProduct, addProduct, updateProduct } from '../../services/dataService';
import { auth } from '../../services/firebase';
import ProductForm from './ProductForm';
import './AdminProducts.css';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchProductsData = async () => {
    try {
      const productsData = await fetchProducts();
      console.log('Fetched products:', productsData);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductsData();
  }, []);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      try {
        console.log('=== DELETE PRODUCT DEBUG ===');
        console.log('Product ID:', productId);
        console.log('Current auth state:', auth.currentUser);
        
        // Test authentication first
        if (!auth.currentUser) {
          alert('ERROR: You are not logged in. Please refresh the page and log in again.');
          return;
        }
        
        console.log('User email:', auth.currentUser.email);
        console.log('User ID:', auth.currentUser.uid);
        console.log('Auth token exists:', !!auth.currentUser.accessToken);
        
        await deleteProduct(productId);
        setProducts(products.filter(p => p.id !== productId));
        alert('Product deleted successfully!');
      } catch (error) {
        console.error('=== DELETE ERROR DETAILS ===');
        console.error('Error object:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        
        const errorMessage = error.message || 'Error deleting product. Please try again.';
        alert(`ERROR: ${errorMessage}\n\nCheck browser console (F12) for more details.`);
      }
    }
  };

  const handleFormSubmit = async (productData) => {
    try {
      console.log('=== HANDLE FORM SUBMIT ===');
      console.log('Editing product:', editingProduct);
      console.log('Product data:', productData);
      console.log('Current user:', auth.currentUser);
      
      if (editingProduct) {
        // Update existing product
        console.log('Updating product:', editingProduct.id);
        await updateProduct(editingProduct.id, {
          ...productData,
          updatedAt: new Date()
        });
        setProducts(products.map(p => 
          p.id === editingProduct.id ? { ...p, ...productData } : p
        ));
        alert('Product updated successfully!');
      } else {
        // Add new product
        console.log('Adding new product...');
        const productId = await addProduct({
          ...productData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        console.log('Product added with ID:', productId);
        setProducts([...products, { id: productId, ...productData }]);
        alert('Product added successfully!');
      }
      setShowForm(false);
      setEditingProduct(null);
      
      // Refresh products list
      await fetchProductsData();
    } catch (error) {
      console.error('=== ERROR SAVING PRODUCT ===');
      console.error('Error object:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      let errorMessage = 'Error saving product. ';
      
      if (error.code === 'permission-denied') {
        errorMessage += 'Permission denied. Please check Firebase security rules and ensure you are logged in.';
      } else if (error.code === 'unavailable') {
        errorMessage += 'Firebase service is unavailable. Please check your internet connection.';
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check the browser console (F12) for more details.';
      }
      
      alert(errorMessage);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.fullDescription && product.fullDescription.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'fertilizers', name: 'Fertilizers' },
    { id: 'seeds', name: 'Seeds' },
    { id: 'tools', name: 'Gardening Tools' },
    { id: 'accessories', name: 'Accessories' }
  ];

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (showForm) {
    return (
      <ProductForm
        product={editingProduct}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingProduct(null);
        }}
      />
    );
  }

  return (
    <div className="admin-products">
      <div className="admin-header">
        <div className="header-content">
          <h2>Product Management</h2>
          <button className="btn btn-primary" onClick={handleAddProduct}>
            + Add New Product
          </button>
        </div>
        
        <div className="admin-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`products-grid ${filteredProducts.length <= 2 ? 'few-items' : ''}`}>
        {filteredProducts.length > 0 ? (
          filteredProducts.map(product => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                <img src={product.image || '/logo.png'} alt={product.name} />
                <div className="product-category">{product.category}</div>
              </div>
              
              <div className="product-content">
                <h3>{product.name}</h3>
                <p>{product.fullDescription || 'No description available'}</p>
                <div className="product-status">
                  <span className={`status ${product.isInStock ? 'in-stock' : 'out-of-stock'}`}>
                    {product.isInStock ? 'In Stock' : 'Out of Stock'}
                  </span>
                  {product.isFeatured && <span className="featured-badge">Featured</span>}
                  {product.isFeatured && (
                    <span className="sequence-badge">
                      Order: {product.featuredSequence || 0}
                    </span>
                  )}
                </div>
                
                <div className="product-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEditProduct(product)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteProduct(product.id)}
                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-products">
            <h3>No products found</h3>
            <p>Try adjusting your search criteria or add a new product.</p>
            <button className="btn btn-primary" onClick={handleAddProduct}>
              Add First Product
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
